import {
  compact,
  flatMap,
  groupBy,
  isEmpty,
  map,
  orderBy,
  partition,
  toLower,
  trim,
  uniq,
} from "lodash/fp";
import { DateTime } from "luxon";

import type { TopicCitationGroup } from "@app/analytics/TopicReport/types";
import type { Topic, TopicEntity } from "@app/analytics/types";
import { toFormattedStringList } from "@app/common/utils";
import { isEmailProvider } from "@app/common/utils/providerUtils";

export const LABEL_FOR_ENTITY_TYPE: { [key in TopicEntity["kind"]]: string } = {
  action_item: "Action Item",
  daily_summary: "Daily Update",
  key_question: "Key Question",
  summary: "Summary",
  citation: "Citation",
  title: "",
  key_question_answer: "Answer",
};

const isSameDate = (date1: DateTime, date2: DateTime) =>
  date1.startOf("day").equals(date2.startOf("day"));

const TIMELINE_ENTITY_KINDS: TopicEntity["kind"][] = [
  "action_item",
  "key_question",
  "daily_summary",
];

interface TimelineEntityGroup {
  id: string;
  date: DateTime;
  update?: TopicEntity;
  entities: TopicEntity[];
}

export const getEntityTimelineItemId = (entity: TopicEntity) =>
  entity.createdOn.startOf("day").toFormat("yyyy-MM-dd");

export const groupEntitiesByDate = (
  entities: TopicEntity[]
): TimelineEntityGroup[] => {
  const entitiesByDate = entities.reduce(
    (obj, entity) => {
      if (!TIMELINE_ENTITY_KINDS.includes(entity.kind)) return obj;

      const entityDate = entity.createdOn.startOf("day");
      const id = getEntityTimelineItemId(entity);
      const dateGroup = obj[id] ?? { id, date: entityDate, entities: [] };
      if (entity.kind === "daily_summary") {
        dateGroup.update = entity;
      } else {
        dateGroup.entities.push(entity);
      }
      return {
        ...obj,
        [id]: dateGroup,
      };
    },
    {} as Record<string, TimelineEntityGroup>
  );

  return orderBy(
    (group) => group.date.toMillis(),
    "desc",
    Object.values(entitiesByDate)
  );
};

export const getThreadOrChannelName = (
  entity?: TopicEntity | Partial<TopicEntity>
) => {
  if (!entity) return "";

  if (isEmailProvider(entity.provider)) {
    return entity.providerThreadName ?? entity.providerChannelName;
  }
  return entity.providerChannelName ?? entity.providerThreadName;
};

export const groupCitationsBySource = (
  citations: TopicEntity[]
): TopicCitationGroup[] => {
  const citationsByProvider = groupBy("provider", citations);

  const groups: TopicCitationGroup[] = [];

  Object.entries(citationsByProvider).forEach(
    ([provider, citationsForProvider]) => {
      const providerIsEmailProvider = isEmailProvider(
        provider as TopicEntity["provider"]
      );
      const groupByField: keyof TopicEntity = providerIsEmailProvider
        ? "providerThreadName"
        : "providerChannelName";

      const alternateField: keyof TopicEntity = providerIsEmailProvider
        ? "providerChannelName"
        : "providerThreadName";

      const citationsByThreadOrChannel = groupBy(
        groupByField,
        citationsForProvider
      );

      const groupedCitations = Object.values(citationsByThreadOrChannel).map(
        (threadOrChannelCitations) => {
          const [populatedCitations, emptyCitations] = partition(
            (c) => Boolean(c.text),
            threadOrChannelCitations
          );

          const defaultCitation = populatedCitations[0] ?? emptyCitations[0];

          const sortedPopulatedCitations = orderBy(
            (c) => c.endTime?.toMillis() ?? 0,
            "desc",
            populatedCitations
          );

          // contributors are only going to show for "empty" citations, so only use the contributors
          // from the empty citations
          const contributors = uniq(
            flatMap((c) => map("displayName", c.contributors), emptyCitations)
          );

          return {
            citations: sortedPopulatedCitations,
            lastUpdateDate: defaultCitation.endTime,
            provider: defaultCitation.provider,
            groupName: (defaultCitation[groupByField] ??
              defaultCitation[alternateField]) as string,
            groupLink: defaultCitation.providerLink,
            contributors,
          };
        }
      );

      groups.push(...groupedCitations);
    }
  );

  return orderBy(
    (group) => group.lastUpdateDate?.toMillis() ?? 0,
    "desc",
    groups
  );
};

const getFormattedDate = (
  startDate: DateTime,
  endDate: DateTime,
  format:
    | Intl.DateTimeFormatOptions
    | {
        primary: Intl.DateTimeFormatOptions;
        secondary: Intl.DateTimeFormatOptions;
      }
) => {
  const isSingleDate = isSameDate(startDate, endDate);

  const hasMultipleFormats = "primary" in format;
  const primaryFormat = hasMultipleFormats ? format.primary : format;
  const secondaryFormat = hasMultipleFormats ? format.secondary : format;

  if (isSingleDate) {
    return startDate.toLocaleString(primaryFormat);
  }

  return `${startDate.toLocaleString(secondaryFormat)}-${endDate.toLocaleString(
    primaryFormat
  )}`;
};

export const getFormattedTopicDateRange = (topic: Topic) =>
  getFormattedDate(topic.firstMention, topic.lastMention, {
    primary: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    secondary: {
      month: "short",
      day: "numeric",
    },
  });

export const getFormattedEntityDate = (entity: TopicEntity) =>
  getFormattedDate(entity.startTime, entity.endTime, {
    month: "short",
    day: "numeric",
  });

export const getEntityCopyContent = (entity?: TopicEntity | string) => {
  if (!entity) return "";

  if (typeof entity === "string") return trim(entity);

  return `${trim(entity.text)} (${getFormattedEntityDate(entity)})`;
};

const getSectionAsString = (
  sectionTitle: string,
  entities?: (TopicEntity | string)[],
  options: Parameters<typeof toFormattedStringList>[1] = { bulleted: true }
) => {
  if (!entities) return null;

  const heading = `${sectionTitle}:\n\n`;

  if (isEmpty(entities)) {
    return `${heading}No ${toLower(sectionTitle)}`;
  }

  const textItems = map(getEntityCopyContent, entities);

  const itemsAsStringList = toFormattedStringList(textItems, options);
  return `${heading}${itemsAsStringList}`;
};

export const getCopyContent = ({
  actionItems,
  keyQuestions,
  dailyUpdates,
}: {
  actionItems?: TopicEntity[];
  keyQuestions?: TopicEntity[];
  dailyUpdates?: TopicEntity[];
}) => {
  return compact([
    getSectionAsString("Daily Updates", dailyUpdates),
    getSectionAsString("Action Items", actionItems),
    getSectionAsString("Key Questions", keyQuestions),
  ]).join("\n\n\n");
};

export const generateSummaryTextFile = (
  topic: Topic,
  entities: {
    actionItems?: TopicEntity[];
    keyQuestions?: TopicEntity[];
    dailyUpdates?: TopicEntity[];
  }
) => {
  const heading = `${topic.title}\n${getFormattedTopicDateRange(topic)}`;
  const summary = getSectionAsString("Summary", [topic.summary], {
    bulleted: false,
  });
  const content = [heading, summary, getCopyContent(entities)].join("\n\n");
  return encodeURIComponent(content);
};
